import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../api';
import './ChatbotWidget.css';

const ChatbotWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: 'Hello! I am the MoU Assistant. How can I help you navigate our partnerships today?' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  // Fetch history on mount if logged in
  useEffect(() => {
    if (user && user.email) {
      fetch(`${API_BASE_URL}/api/ai/chat/history?email=${encodeURIComponent(user.email)}`)
        .then(res => res.json())
        .then(data => {
          if (data.history && data.history.length > 0) {
            // Keep the welcome message, then append history
            const historyMsgs = data.history.map(msg => ({
              role: msg.role === 'bot' || msg.role === 'assistant' ? 'bot' : 'user',
              text: msg.text
            }));
            setMessages([
              { role: 'bot', text: 'Hello! I am the MoU Assistant. How can I help you navigate our partnerships today?' },
              ...historyMsgs
            ]);
          }
        })
        .catch(err => console.error("Failed to load chat history", err));
    }
  }, [user]);

  const handleDeleteHistory = async () => {
    if (!user || !user.email) return;
    const confirmDelete = window.confirm("Are you sure you want to completely delete your chat history? This cannot be undone.");
    if (!confirmDelete) return;

    try {
      await fetch(`${API_BASE_URL}/api/ai/chat/history?email=${encodeURIComponent(user.email)}`, {
        method: 'DELETE'
      });
      setMessages([{ role: 'bot', text: 'Hello! I am the MoU Assistant. How can I help you navigate our partnerships today?' }]);
    } catch (err) {
      console.error("Failed to delete history", err);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userText = inputValue;
    const userMsg = { role: 'user', text: userText };
    
    // Prepare history for API (excluding the welcome message and mapping 'bot' to 'model')


    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userText,
          email: user?.email
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch response');
      }

      const data = await response.json();
      setMessages((prev) => [...prev, { role: 'bot', text: data.text }]);
    } catch (err) {
      console.error('Chat API Error:', err);
      setMessages((prev) => [...prev, { role: 'bot', text: 'Sorry, I encountered an error connecting to my brain. Please try again later.' }]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="chatbot-wrapper">
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-title">
              <span className="chatbot-avatar">🤖</span>
              <div>
                <h3>MoU Assistant</h3>
                <span className="chatbot-status">Online</span>
              </div>
            </div>
            <div className="chatbot-actions">
              <button className="chatbot-delete-btn" onClick={handleDeleteHistory} title="Delete Chat History">🗑️</button>
              <button className="chatbot-close-btn" onClick={() => setIsOpen(false)}>✕</button>
            </div>
          </div>

          <div className="chatbot-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-bubble-container ${msg.role === 'user' ? 'user-container' : 'bot-container'}`}>
                {msg.role === 'bot' && <div className="chat-bubble-avatar">🤖</div>}
                <div className={`chat-bubble ${msg.role}`}>
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="chat-bubble-container bot-container">
                <div className="chat-bubble-avatar">🤖</div>
                <div className="chat-bubble bot typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="chatbot-input-area" onSubmit={handleSend}>
            <input
              type="text"
              placeholder="Ask about MoUs..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <button type="submit" disabled={!inputValue.trim()}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </form>
        </div>
      )}

      {!isOpen && (
        <button className="chatbot-toggle-btn" onClick={() => setIsOpen(true)}>
          💬
        </button>
      )}
    </div>
  );
};

export default ChatbotWidget;
